/**
 * SharedProjectPage Component
 */

import { replaceQueue } from '../../../redux/actions/player';
import { pushPostAuthenticationRequestJoinProject } from '../../../redux/actions/postAuthenticationRequests';
import { requestJoinProject } from '../../../redux/actions/projects';
import { toggleLogInFormVisibility } from '../../../redux/actions/ui';
import { getLicenseString, setLicenseFromParams } from '../../../redux/helpers/license';
import { getPlayerTracksFromResults } from '../../../redux/helpers/player';
import { getLicenseComponents } from '../../../redux/selectors/staticData';
import { ShareProjectFragment } from '../../../relay/project';
import { baseline } from '../../styles/grid';
import './styles.scss';

import { RouteMatch } from 'found';
import { identity, isEmpty, pick, pickBy } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';

import Page from '../../../containers/Page';
import Button from '../../atoms/buttons/Button';
import { IconDownload, IconProjectBox, IconUserAdd } from '../../atoms/images/Icon';
import Heading from '../../atoms/text/Heading';
import { Span } from '../../atoms/text/Label';
import LoadingButton, { ILoadingButtonProps } from '../../molecules/buttons/LoadingButton';
import { ProjectPagePath } from '../Projects';
import { SharedProjectTrackList } from './SharedProjectTrackList';

// Page Path
export const SharedProjectPagePath = (opaqueId: string) => `/playlist/${opaqueId}`;

// Page Props
interface IOwnStateProps {
  isUserLoggedIn: boolean;
  licenseComponents: GQL.ILicenseComponents;
}

interface IOwnDispatchProps {
  setPlayerQueue(tracks: IPlayerTrack[]): void;
}

interface IOwnMergedProps {
  joinProject(): Promise<GQL.IMutation>;
  logInToJoinProject(): void;
}

interface IOwnProps extends RouteMatch {
  viewer: GQL.IViewer;
}

type SharedProjectPageProps = IOwnProps & IOwnStateProps & IOwnDispatchProps & IOwnMergedProps;

interface IOwnState {
  redirecting: boolean;
  isProcessingJoinMutation: boolean;
}

const classes = new BEM('shared-project');
const defaultList = [];

class SharedProjectPage extends React.Component<SharedProjectPageProps, IOwnState> {
  constructor(props: SharedProjectPageProps) {
    super(props);

    this.state = {
      isProcessingJoinMutation: false,
      redirecting: false,
    };
  }

  componentDidMount() {
    const {
      viewer: {
        shared_project,
      },
    } = this.props;

    if (shared_project) {
      if (this.isUserACollaborator()) {
        this.goToProjectPage();
      } else {
        this.props.setPlayerQueue(
          getPlayerTracksFromResults(shared_project.tracks!.edges!.map((e) => e.node!) || defaultList),
        );
      }
    }
  }

  componentDidUpdate({ viewer : { shared_project: prevSharedProject }}: SharedProjectPageProps) {
    const { viewer: { shared_project: sharedProject } } = this.props;
    if (sharedProject) {
      if (prevSharedProject) {
        if (sharedProject.users!.length !== prevSharedProject.users!.length) {
          if (this.isUserACollaborator()) {
            this.goToProjectPage();
          }
        }
      } else if (this.isUserACollaborator()) {
        this.goToProjectPage();
      }
    }
  }

  onJoinProject = async () => {
    try {
      this.setState({ isProcessingJoinMutation: true });
      await this.props.joinProject();
      setTimeout(() => this.goToProjectPage, 500);
    } catch (error) {
      console.error(error);
    }
  }

  renderProject() {
    const project = this.props.viewer.shared_project!;
    const hasOwner = !isEmpty(pickBy(pick(project.owner, ['first_name', 'last_name']), identity));
    const owner = `${project.owner!.first_name} ${project.owner!.last_name}`;
    const projectHasTerms = !!project.license_params!.length;

    const usages = projectHasTerms ?
      setLicenseFromParams(this.props.licenseComponents, project.license_params!)
      : defaultList;

    const joinProjectButtonProps: Partial<ILoadingButtonProps> = {
      label: 'Join This Playlist',
      size: 'large',
      colorScheme: 'accent',
      variant: 'pill',
      withShadow: !this.state.isProcessingJoinMutation,
      icon: <IconUserAdd/>,
      ...classes('join-button'),
    };

    return (
      <>
        <Helmet title={`${project.name} — Owned by ${owner}`} />
        <IconProjectBox {...classes('icon')} />
        <Heading is="h6" {...classes('identification')}>Playlist</Heading>
        {hasOwner && <Heading is="h5" textColor="bw6" {...classes('shared-by')}>Owned by {owner}</Heading>}
        <Heading is="h1" {...classes('title')}>{project.name}</Heading>
        {projectHasTerms &&
          <Span size="large" variant="description" textColor="bw7" {...classes('license-string')}>
            {getLicenseString(usages)}
          </Span>
        }
        <div {...classes('buttons')}>
          {this.props.isUserLoggedIn ? (
            <LoadingButton
              onClick={this.onJoinProject}
              isLoading={this.state.isProcessingJoinMutation}
              {...joinProjectButtonProps}
            />
          ) : (
            <Button
              onClick={this.props.logInToJoinProject}
              {...joinProjectButtonProps}
            />
            )}
          <Button
            wrapperComponent="a"
            hyperLinkProps={{
              href: `/playlist/${project.opaque_id}/download`,
              target: '_blank',
            }}
            label="Download Tracks"
            // colorScheme="transparent"
            size="large"
            colorScheme="light"
            variant="pill"
            withShadow
            icon={<IconDownload />}
            {...classes('download-button')}
          />
        </div>
        {project.tracks && project.tracks.edges && project.tracks.edges.length ?
          <SharedProjectTrackList license={usages} tracks={project.tracks} />
          : (
            <div style={{ marginTop: baseline * 10 }}>
              <Heading is="h3" textColor="bw6" {...classes('no-tracks')}>
                There are no tracks in this playlist.
              </Heading>
            </div>
          )}
      </>
    );
  }

  render() {
    const project = this.props.viewer.shared_project;

    return (
      <Page id="shared-project" {...classes() } noIndex>
      {this.state.redirecting ? (
        <div {...classes('content-wrapper')}>
          <Heading is="h2" {...classes('title', 'redirecting')}>Redirecting to playlist page...</Heading>
        </div>
      ) : (
        <div {...classes('content-wrapper')}>
          {project ? this.renderProject() : (
            <Heading is="h2" {...classes('title', 'redirecting')}>playlist not found</Heading>
          )}
        </div>
      )}
      </Page>
    );
  }

  private goToProjectPage() {
    this.setState({ redirecting: true }, () => {
      this.props.router.push(ProjectPagePath(this.props.viewer.shared_project!.id));
    });
  }

  private isUserACollaborator() {
    const { viewer: { profile, shared_project } } = this.props;
    return shared_project && profile ?
      shared_project.users!.some(({ user_id }) => user_id === profile.user_id)
      : undefined;
  }
}

const ConnectedSharedProjectPage =
connect<IOwnStateProps, IOwnDispatchProps & IDispatchProps, IOwnProps, SharedProjectPageProps, IStoreState>(
  (state) => ({
    isUserLoggedIn: !!state.user.profile,
    licenseComponents: getLicenseComponents(state),
  }),
  (dispatch) => ({
    dispatch,
    setPlayerQueue: (tracks) => {
      dispatch(replaceQueue({ tracks }));
    },
  }),
  (stateProps, { dispatch, ...dispatchProps}, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    joinProject: () => new Promise((resolve, reject) => {
      dispatch(requestJoinProject({
        input: { opaque_id: ownProps.viewer.shared_project!.opaque_id! },
        promise: { resolve, reject },
      }));
    }),
    logInToJoinProject: () => {
      const projectOpaqueId = ownProps.viewer.shared_project!.opaque_id!;
      dispatch(pushPostAuthenticationRequestJoinProject({
        projectOpaqueId,
        page: SharedProjectPagePath(projectOpaqueId),
      }));
      dispatch(toggleLogInFormVisibility(true));
    },
  }),
)(SharedProjectPage);

const RelaySharedProjectsPage = createFragmentContainer(
  ConnectedSharedProjectPage,
  ShareProjectFragment,
);

export default RelaySharedProjectsPage;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/SharedProject/index.tsx